/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @angular-eslint/no-output-on-prefix */
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  effect,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import {
  InputLabelOptions,
  TypographyKeys,
  WidgetStyleProps,
} from '@finxone-platform/shared/sys-config-types';
import { PrimeTemplate } from 'primeng/api';
import { DomHandler } from 'primeng/dom';
import { Caret } from './finx-inputmask.interface';

export declare type Nullable<T = void> = T | null | undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const INPUTMASK_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => FinxInputMaskComponent),
  multi: true,
};
/**
 * InputMask component is used to enter input in a certain format such as numeric, date, currency, email and phone.
 * @group Components
 */
@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finx-inputmask',
  template: `
    <label
      *ngIf="label && labelType !== labelTypeOptions.PLACEHOLDER"
      [class.labelActive]="isInputOnFocus"
      [class.floatingLabelWithValue]="value?.length && labelType === labelTypeOptions.FLOATING"
      [class.floatingLabel]="labelType === labelTypeOptions.FLOATING"
      class="labelText labelIdle labelConfig"
      [for]="name"
      >{{ label }}</label
    >
    <input
      hlmInput
      #input
      finx-input
      class="finx-inputmask"
      [attr.id]="inputId"
      [attr.type]="type"
      [attr.name]="name"
      [ngClass]="styleClass + ' ' + textStylingClass"
      [attr.placeholder]="labelType === labelTypeOptions.PLACEHOLDER ? label : placeholder ?? ''"
      [attr.title]="title"
      [attr.size]="size"
      [attr.autocomplete]="autocomplete"
      [attr.maxlength]="maxlength"
      [attr.tabindex]="tabindex"
      [attr.aria-label]="ariaLabel"
      [attr.aria-labelledBy]="ariaLabelledBy"
      [attr.aria-required]="ariaRequired"
      [disabled]="disabled"
      [readonly]="readonly"
      [class.disabled-input]="readonly"
      [attr.required]="required"
      (focus)="onInputFocus($event); isInputOnFocus = true"
      (blur)="onInputBlur($event); isInputOnFocus = false"
      (keydown)="onInputKeydown($event)"
      (keypress)="onKeyPress($event)"
      pAutoFocus
      [autofocus]="autoFocus"
      (input)="onInputChange($event)"
      (paste)="handleInputChange($event)"
      [attr.data-pc-name]="'inputmask'"
      [attr.data-pc-section]="'root'"
      [attr.inputmode]="inputMode"
    />
  `,
  providers: [INPUTMASK_VALUE_ACCESSOR],
  styleUrls: ['./finx-inputmask.component.scss'],
})
export class FinxInputMaskComponent implements OnInit, ControlValueAccessor, AfterContentInit {
  private document = inject<Document>(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  el = inject(ElementRef);
  cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);

  @Input() label = '';
  @Input() labelAsPlaceholder?: boolean;
  @Input() colorTextAlignStyle: WidgetStyleProps;
  /**
   * HTML5 input type.
   * @group Props
   */
  @Input() type = 'text';
  /**
   * Placeholder character in mask, default is underscore.
   * @group Props
   */
  @Input() slotChar = '_';
  /**
   * Clears the incomplete value on blur.
   * @group Props
   */
  @Input() autoClear = true;
  /**
   * When enabled, a clear icon is displayed to clear the value.
   * @group Props
   */
  @Input() showClear = false;
  /**
   * Inline style of the input field.
   * @group Props
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() style: { [klass: string]: any } | null | undefined;
  /**
   * Identifier of the focus input to match a label defined for the component.
   * @group Props
   */
  @Input() inputId: string | undefined;
  /**
   * Style class of the input field.
   * @group Props
   */
  @Input() styleClass: string | undefined;
  /**
   * Advisory information to display on input.
   * @group Props
   */
  @Input() placeholder: string | undefined;
  /**
   * Size of the input field.
   * @group Props
   */
  @Input() size: number | undefined;
  /**
   * Maximum number of character allows in the input field.
   * @group Props
   */
  @Input() maxlength: number | undefined;
  /**
   * Specifies tab order of the element.
   * @group Props
   */
  @Input() tabindex: string | undefined;
  /**
   * Title text of the input text.
   * @group Props
   */
  @Input() title: string | undefined;
  /**
   * Used to define a string that labels the input element.
   * @group Props
   */
  @Input() ariaLabel: string | undefined;
  /**
   * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
   * @group Props
   */
  @Input() ariaLabelledBy: string | undefined;
  /**
   * Used to indicate that user input is required on an element before a form can be submitted.
   * @group Props
   */
  @Input() ariaRequired: boolean | undefined;
  /**
   * When present, it specifies that the element value cannot be altered.
   * @group Props
   */
  @Input() disabled: boolean | undefined;
  /**
   * When present, it specifies that an input field is read-only.
   * @group Props
   */
  @Input() readonly: boolean | undefined;
  /**
   * Defines if ngModel sets the raw unmasked value to bound value or the formatted mask value.
   * @group Props
   */
  @Input() unmask: boolean | undefined;
  /**
   * Name of the input field.
   * @group Props
   */
  @Input() name: string | undefined;
  /**
   * When present, it specifies that an input field must be filled out before submitting the form.
   * @group Props
   */
  @Input() required: boolean | undefined;
  /**
   * Regex pattern for alpha characters
   * @group Props
   */
  @Input() characterPattern = '[A-Za-z]';
  /**
   * When present, the input gets a focus automatically on load.
   * @group Props
   */
  @Input() autoFocus: boolean | undefined;
  /**
   * Used to define a string that autocomplete attribute the current element.
   * @group Props
   */
  @Input() autocomplete: string | undefined;
  /**
   * When present, it specifies that whether to clean buffer value from model.
   * @group Props
   */
  @Input() keepBuffer = false;

  /**
   * The inputmode global attribute is an enumerated attribute
   * that hints at the type of data that might be entered
   * by the user while editing the element or its contents.
   * This allows a browser to display an appropriate virtual keyboard
   */
  @Input() inputMode = 'text';

  @Output() focusInput = new EventEmitter<boolean>();
  /**
   * Mask pattern.
   * @group Props
   */
  @Input() get mask(): string | undefined | null {
    return this._mask;
  }

  set mask(val: string | undefined | null) {
    this._mask = val;

    this.initMask();
    this.writeValue('');
    this.onModelChange(this.value);
  }
  /**
   * Callback to invoke when the mask is completed.
   * @group Emits
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() onComplete: EventEmitter<any> = new EventEmitter<any>();
  /**
   * Callback to invoke when the component receives focus.
   * @param {Event} event - Browser event.
   * @group Emits
   */
  @Output() onFocus: EventEmitter<Event> = new EventEmitter<Event>();
  /**
   * Callback to invoke when the component loses focus.
   * @param {Event} event - Browser event.
   * @group Emits
   */
  @Output() onBlur: EventEmitter<Event> = new EventEmitter<Event>();
  /**
   * Callback to invoke on input.
   * @param {Event} event - Browser event.
   * @group Emits
   */
  @Output() onInput: EventEmitter<Event> = new EventEmitter<Event>();
  /**
   * Callback to invoke on input key press.
   * @param {Event} event - Browser event.
   * @group Emits
   */
  @Output() onKeydown: EventEmitter<Event> = new EventEmitter<Event>();
  /**
   * Callback to invoke when input field is cleared.
   * @group Emits
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() onClear: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('input', { static: true }) inputViewChild: Nullable<ElementRef>;

  @ContentChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;

  value: Nullable<string>;

  _mask: Nullable<string>;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onModelChange: Function = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onModelTouched: Function = () => {};

  input: Nullable<HTMLInputElement>;

  filled: Nullable<boolean>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defs: Nullable<{ [klass: string]: any }>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tests: RegExp[] | any;

  partialPosition: Nullable<number>;

  firstNonMaskPos: Nullable<number>;

  lastRequiredNonMaskPos: Nullable<number>;

  len: Nullable<number>;

  oldVal: Nullable<string>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buffer: string[] | any;

  defaultBuffer: Nullable<string>;

  focusText: Nullable<string>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  caretTimeoutId: any;

  androidChrome = true;

  focused: Nullable<boolean>;

  private cssVars: Record<string, string> = {};
  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public textStylingClass: TypographyKeys;
  public isInputOnFocus = false;

  constructor() {
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      if (globalCssVariables) {
        this.cssVars = globalCssVariables;
        //get the label type and text style class from root styles
        this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
        this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
      }
    });
  }

  ngOnInit() {
    this.colorTextAlignStyle = this.colorTextAlignStyle || {};
    if (isPlatformBrowser(this.platformId)) {
      const ua = navigator.userAgent;
      this.androidChrome = /chrome/i.test(ua) && /android/i.test(ua);
    }

    this.initMask();
  }

  ngAfterContentInit() {
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    this.templates.forEach((item) => {});
  }
  initMask() {
    this.tests = [];
    this.partialPosition = (this.mask as string).length;
    this.len = (this.mask as string).length;
    this.firstNonMaskPos = null;
    this.defs = {
      '9': '[0-9]',
      a: this.characterPattern,
      '*': `${this.characterPattern}|[0-9]`,
    };

    const maskTokens = (this.mask as string).split('');
    for (let i = 0; i < maskTokens.length; i++) {
      const c = maskTokens[i];
      if (c == '?') {
        this.len--;
        this.partialPosition = i;
      } else if (this.defs[c]) {
        this.tests.push(new RegExp(this.defs[c]));
        if (this.firstNonMaskPos === null) {
          this.firstNonMaskPos = this.tests.length - 1;
        }
        if (i < this.partialPosition) {
          this.lastRequiredNonMaskPos = this.tests.length - 1;
        }
      } else {
        this.tests.push(null);
      }
    }

    this.buffer = [];
    for (let i = 0; i < maskTokens.length; i++) {
      const c = maskTokens[i];
      if (c != '?') {
        if (this.defs[c]) this.buffer.push(this.getPlaceholder(i));
        else this.buffer.push(c);
      }
    }
    this.defaultBuffer = this.buffer.join('');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(value: any): void {
    this.value = value;

    if (this.inputViewChild && this.inputViewChild.nativeElement) {
      if (this.value == undefined || this.value == null) this.inputViewChild.nativeElement.value = '';
      else this.inputViewChild.nativeElement.value = this.value;

      this.checkVal();
      this.focusText = this.inputViewChild.nativeElement.value;
      this.updateFilledState();
    }
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
    this.cd.markForCheck();
  }

  caret(first?: number, last?: number): Caret | undefined {
    let range, begin, end;

    if (
      !this.inputViewChild?.nativeElement.offsetParent ||
      this.inputViewChild.nativeElement !== this.inputViewChild.nativeElement.ownerDocument.activeElement
    ) {
      return;
    }

    if (typeof first == 'number') {
      begin = first;
      end = typeof last === 'number' ? last : begin;
      if (this.inputViewChild.nativeElement.setSelectionRange) {
        this.inputViewChild.nativeElement.setSelectionRange(begin, end);
      } else if (this.inputViewChild.nativeElement['createTextRange']) {
        range = this.inputViewChild.nativeElement['createTextRange']();
        range.collapse(true);
        range.moveEnd('character', end);
        range.moveStart('character', begin);
        range.select();
      }
    } else {
      if (this.inputViewChild.nativeElement.setSelectionRange) {
        begin = this.inputViewChild.nativeElement.selectionStart;
        end = this.inputViewChild.nativeElement.selectionEnd;
      } else if (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.document as any['selection']) &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.document as any)['selection'].createRange
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        range = (this.document as any['selection']).createRange();
        begin = 0 - range.duplicate().moveStart('character', -100000);
        end = begin + range.text.length;
      }

      return { begin: begin, end: end };
    }
    return undefined;
  }

  isCompleted(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let completed: boolean;
    for (let i = this.firstNonMaskPos as number; i <= (this.lastRequiredNonMaskPos as number); i++) {
      if (this.tests[i] && (this.buffer as string[])[i] === this.getPlaceholder(i)) {
        return false;
      }
    }

    return true;
  }

  getPlaceholder(i: number) {
    if (i < this.slotChar.length) {
      return this.slotChar.charAt(i);
    }
    return this.slotChar.charAt(0);
  }

  seekNext(pos: number) {
    while (++pos < (this.len as number) && !this.tests[pos]);
    return pos;
  }

  seekPrev(pos: number) {
    while (--pos >= 0 && !this.tests[pos]);
    return pos;
  }

  shiftL(begin: number, end: number) {
    let i, j;

    if (begin < 0) {
      return;
    }

    for (i = begin, j = this.seekNext(end); i < (this.len as number); i++) {
      if (this.tests[i]) {
        if (j < (this.len as number) && this.tests[i].test(this.buffer[j])) {
          this.buffer[i] = this.buffer[j];
          this.buffer[j] = this.getPlaceholder(j);
        } else {
          break;
        }

        j = this.seekNext(j);
      }
    }
    this.writeBuffer();
    this.caret(Math.max(this.firstNonMaskPos as number, begin));
  }

  shiftR(pos: number) {
    let i, c, j, t;

    for (i = pos, c = this.getPlaceholder(pos); i < (this.len as number); i++) {
      if (this.tests[i]) {
        j = this.seekNext(i);
        t = this.buffer[i];
        this.buffer[i] = c;
        if (j < (this.len as number) && this.tests[j].test(t)) {
          c = t;
        } else {
          break;
        }
      }
    }
  }

  handleAndroidInput(e: Event) {
    const curVal = this.inputViewChild?.nativeElement.value;
    const pos = this.caret() as Caret;
    if (this.oldVal && this.oldVal.length && this.oldVal.length > curVal.length) {
      // a deletion or backspace happened
      this.checkVal(true);
      while (pos.begin > 0 && !this.tests[pos.begin - 1]) pos.begin--;
      if (pos.begin === 0) {
        while (pos.begin < (this.firstNonMaskPos as number) && !this.tests[pos.begin]) pos.begin++;
      }

      setTimeout(() => {
        this.caret(pos.begin, pos.begin);
        this.updateModel(e);
        if (this.isCompleted()) {
          this.onComplete.emit();
        }
      }, 0);
    } else {
      this.checkVal(true);
      while (pos.begin < (this.len as number) && !this.tests[pos.begin]) pos.begin++;

      setTimeout(() => {
        this.caret(pos.begin, pos.begin);
        this.updateModel(e);
        if (this.isCompleted()) {
          this.onComplete.emit();
        }
      }, 0);
    }
  }

  onInputBlur(e: Event) {
    this.focused = false;
    this.focusInput.emit(this.focused);
    this.onModelTouched();
    if (!this.keepBuffer) {
      this.checkVal();
    }
    this.updateFilledState();
    this.onBlur.emit(e);

    if (
      this.inputViewChild?.nativeElement.value != this.focusText ||
      this.inputViewChild?.nativeElement.value != this.value
    ) {
      this.updateModel(e);
      const event = this.document.createEvent('HTMLEvents');
      event.initEvent('change', true, false);
      this.inputViewChild?.nativeElement.dispatchEvent(event);
    }
  }

  onInputKeydown(e: KeyboardEvent) {
    if (this.readonly) {
      return;
    }

    // eslint-disable-next-line prefer-const
    let k = e.which || e.keyCode,
      pos,
      begin,
      end;
    let iPhone;
    if (isPlatformBrowser(this.platformId)) {
      iPhone = /iphone/i.test(navigator.userAgent);
    }
    this.oldVal = this.inputViewChild?.nativeElement.value;

    this.onKeydown.emit(e);

    //backspace, delete, and escape get special treatment
    if (k === 8 || k === 46 || (iPhone && k === 127)) {
      pos = this.caret() as Caret;
      begin = pos.begin;
      end = pos.end;

      if (end - begin === 0) {
        begin = k !== 46 ? this.seekPrev(begin) : (end = this.seekNext(begin - 1));
        end = k === 46 ? this.seekNext(end) : end;
      }

      this.clearBuffer(begin, end);
      if (this.keepBuffer) {
        this.shiftL(begin, end - 2);
      } else {
        this.shiftL(begin, end - 1);
      }
      this.updateModel(e);
      this.onInput.emit(e);

      e.preventDefault();
    } else if (k === 13) {
      // enter
      this.onInputBlur(e);
      this.updateModel(e);
    } else if (k === 27) {
      // escape
      (this.inputViewChild as ElementRef).nativeElement.value = this.focusText;
      this.caret(0, this.checkVal());
      this.updateModel(e);

      e.preventDefault();
    }
  }

  onKeyPress(e: KeyboardEvent) {
    if (this.readonly) {
      return;
    }

    // eslint-disable-next-line prefer-const
    let k = e.which || e.keyCode,
      // eslint-disable-next-line prefer-const
      pos = this.caret() as Caret,
      p: number,
      c: string,
      next: number,
      completed!: boolean;

    if (e.ctrlKey || e.altKey || e.metaKey || k < 32 || (k > 34 && k < 41)) {
      //Ignore
      return;
    } else if (k && k !== 13) {
      if (pos.end - pos.begin !== 0) {
        this.clearBuffer(pos.begin, pos.end);
        this.shiftL(pos.begin, pos.end - 1);
      }

      p = this.seekNext(pos.begin - 1);
      if (p < (this.len as number)) {
        c = String.fromCharCode(k);
        if (this.tests[p].test(c)) {
          this.shiftR(p);

          this.buffer[p] = c;
          this.writeBuffer();
          next = this.seekNext(p);

          if (DomHandler.isClient() && /android/i.test(navigator.userAgent)) {
            const proxy = () => {
              this.caret(next);
            };

            setTimeout(proxy, 0);
          } else {
            this.caret(next);
          }

          if (pos.begin <= (this.lastRequiredNonMaskPos as number)) {
            completed = this.isCompleted();
          }

          this.onInput.emit(e);
        }
      }
      e.preventDefault();
    }

    this.updateModel(e);

    this.updateFilledState();

    if (completed) {
      this.onComplete.emit();
    }
  }

  clearBuffer(start: number, end: number) {
    if (!this.keepBuffer) {
      let i;
      for (i = start; i < end && i < (this.len as number); i++) {
        if (this.tests[i]) {
          this.buffer[i] = this.getPlaceholder(i);
        }
      }
    }
  }

  writeBuffer() {
    (this.inputViewChild as ElementRef).nativeElement.value = this.buffer.join('');
  }

  checkVal(allow?: boolean): number {
    //try to place characters where they belong
    // eslint-disable-next-line prefer-const
    let test = this.inputViewChild?.nativeElement.value,
      lastMatch = -1,
      i,
      c,
      pos;

    for (i = 0, pos = 0; i < (this.len as number); i++) {
      if (this.tests[i]) {
        this.buffer[i] = this.getPlaceholder(i);
        while (pos++ < test.length) {
          c = test.charAt(pos - 1);
          if (this.tests[i].test(c)) {
            if (!this.keepBuffer) {
              this.buffer[i] = c;
            }
            lastMatch = i;
            break;
          }
        }
        if (pos > test.length) {
          this.clearBuffer(i + 1, this.len as number);
          break;
        }
      } else {
        if (this.buffer[i] === test.charAt(pos)) {
          pos++;
        }
        if (i < (this.partialPosition as number)) {
          lastMatch = i;
        }
      }
    }
    if (allow) {
      this.writeBuffer();
    } else if (lastMatch + 1 < (this.partialPosition as number)) {
      if (this.autoClear || this.buffer.join('') === this.defaultBuffer) {
        // Invalid value. Remove it and replace it with the
        // mask, which is the default behavior.
        if (this.inputViewChild?.nativeElement.value) this.inputViewChild.nativeElement.value = '';
        this.clearBuffer(0, this.len as number);
      } else {
        // Invalid value, but we opt to show the value to the
        // user and allow them to correct their mistake.
        this.writeBuffer();
      }
    } else {
      this.writeBuffer();
      (this.inputViewChild as ElementRef).nativeElement.value =
        this.inputViewChild?.nativeElement.value.substring(0, lastMatch + 1);
    }
    return (this.partialPosition ? i : this.firstNonMaskPos) as number;
  }

  onInputFocus(event: Event) {
    if (this.readonly) {
      return;
    }

    this.focused = true;
    this.focusInput.emit(this.focused);
    clearTimeout(this.caretTimeoutId);
    let pos: number;

    this.focusText = this.inputViewChild?.nativeElement.value;

    // eslint-disable-next-line prefer-const
    pos = this.keepBuffer ? this.inputViewChild?.nativeElement.value.length : this.checkVal();

    this.caretTimeoutId = setTimeout(() => {
      if (
        this.inputViewChild?.nativeElement !== this.inputViewChild?.nativeElement.ownerDocument.activeElement
      ) {
        return;
      }
      this.writeBuffer();
      if (pos == this.mask?.replace('?', '').length) {
        this.caret(0, pos);
      } else {
        this.caret(pos);
      }
    }, 50);

    this.onFocus.emit(event);
  }

  onInputChange(event: Event) {
    if (this.androidChrome) this.handleAndroidInput(event);
    else this.handleInputChange(event);

    this.onInput.emit(event);
  }

  handleInputChange(event: Event) {
    if (this.readonly) {
      return;
    }

    setTimeout(() => {
      const pos = this.checkVal(true);
      this.caret(pos);
      this.updateModel(event);
      if (this.isCompleted()) {
        this.onComplete.emit();
      }
    }, 0);
  }

  getUnmaskedValue() {
    const unmaskedBuffer = [];
    for (let i = 0; i < this.buffer.length; i++) {
      const c = this.buffer[i];
      if (this.tests[i] && c != this.getPlaceholder(i)) {
        unmaskedBuffer.push(c);
      }
    }

    return unmaskedBuffer.join('');
  }

  updateModel(e: Event) {
    const updatedValue = this.unmask ? this.getUnmaskedValue() : (e.target as HTMLInputElement).value;
    if (updatedValue !== null || updatedValue !== undefined) {
      this.value = updatedValue;
      this.onModelChange(this.value);
    }
  }

  updateFilledState() {
    this.filled = this.inputViewChild?.nativeElement && this.inputViewChild.nativeElement.value != '';
  }

  focus() {
    this.inputViewChild?.nativeElement.focus();
  }

  clear() {
    (this.inputViewChild as ElementRef).nativeElement.value = '';
    this.value = null;
    this.onModelChange(this.value);
    this.onClear.emit();
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }
}
